import {
  ArrowLeftOutlined,
  CameraOutlined,
  FileTextOutlined,
  InboxOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Modal, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  TiendaPerfectaFormStep,
  TiendaPerfectaStopDetail,
} from 'models/TiendaPerfecta';

import { useRouteReportTheme } from '../hooks/useRouteReportTheme';
import {
  hasDeliveredMaterials,
  hasMaterialsStep,
  hasStopMaterials,
} from '../utils/stopDetail';
import { formatDate, formatTime } from '../utils/time';
import StopMaterialsTable from './StopMaterialsTable';
import StopPhotoGrid from './StopPhotoGrid';

const I18N_BASE = 'scenes.route_report.components.stop_activity';

/** Qué actividad se está mirando. Son dos y son fijas. */
export type StopActivityKey = 'pop' | 'incident';

const STEP_ICONS: Record<string, JSX.Element> = {
  photo: <CameraOutlined />,
  materials: <InboxOutlined />,
  list: <UnorderedListOutlined />,
  text: <FileTextOutlined />,
};

interface StepBodyProps {
  step: TiendaPerfectaFormStep;
  detail: TiendaPerfectaStopDetail;
}

/**
 * El contenido de un paso, según su tipo.
 *
 * Cada `type` llena sólo sus campos y deja el resto en `null`: la ausencia de
 * `placeholder` en un paso de foto no es un dato faltante, es el tipo del paso.
 */
function StepBody({ step, detail }: StepBodyProps) {
  const { t } = useTranslation();

  /* Los materiales van ANTES del guard de `answer`: no viven en el `answer` de
     este paso sino en el bloque `materials` del payload, así que el paso puede
     tener `answer: null` y aun así haber material que mostrar. Preguntarle por
     su respuesta a un paso que no la usa escondía la tabla entera. */
  if (step.type === 'materials') {
    return <StopMaterialsTable materials={detail.materials} />;
  }

  /* Un `answer` nulo NO significa "el paso no existe": existe y está esperando
     respuesta. Una parada pendiente publica su plantilla entera así. El tag
     "Sin responder" del encabezado ya lo dice, así que el cuerpo va vacío. */
  if (step.answer === null) return null;

  if (step.type === 'photo') {
    return <StopPhotoGrid photos={step.answer.photos} />;
  }

  if (step.type === 'list') {
    return (
      <>
        <div className="stop-step__chips">
          {step.answer.selections.map((selection) => (
            <Tag color="purple" key={selection.itemTitle}>
              {selection.itemTitle}
            </Tag>
          ))}
        </div>

        {step.answer.otherValue && (
          <div className="stop-step__text">
            <span className="stop-step__text-label">
              {t(`${I18N_BASE}.other_label`)}
            </span>
            {step.answer.otherValue}
          </div>
        )}
      </>
    );
  }

  if (step.type === 'text') {
    return <div className="stop-step__text">{step.answer.textValue}</div>;
  }

  /* Rama por defecto para un `type` que upstream agregue más adelante. Sin
     ella el paso caía en el renderizador de texto y mostraba un cuerpo vacío
     —`textValue` es `null` en cualquier tipo que no sea `text`— sin decir que
     no se pudo mostrar. El contrato pide expresamente ramificar con un
     default. */
  return (
    <div className="stop-step__hint">{t(`${I18N_BASE}.unknown_step_type`)}</div>
  );
}

interface StopActivityModalProps {
  activity: StopActivityKey | null;
  detail: TiendaPerfectaStopDetail | null;
  userTimeZone: string;
  onClose: () => void;
}

/**
 * El detalle de UNA actividad de la parada, encima del modal de la parada.
 *
 * No tiene bloque de carga propio: `/stops/{id}` contesta el payload entero de
 * una vez, así que cuando la card de la actividad se puede tocar, su detalle ya
 * está en memoria. Lo único que sigue descargándose son las fotos, y ésas
 * tienen su propio estado dentro de `StopPhotoGrid`.
 */
function StopActivityModal({
  activity,
  detail,
  userTimeZone,
  onClose,
}: StopActivityModalProps) {
  const { t } = useTranslation();
  const themeVars = useRouteReportTheme();

  if (!detail || !activity) return null;

  const isPop = activity === 'pop';
  const incident = detail.incident;

  const title = t(
    `scenes.route_report.components.stop_detail.activities.${activity}`
  );

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={760}
      className="stop-activity-modal"
      /* Mismas variables de marca que el modal de la parada: los dos se portean
         a `document.body` y ninguno hereda las de la vista. */
      styles={{ content: themeVars }}
      title={
        <div>
          <button
            type="button"
            className="stop-activity__back"
            onClick={onClose}
          >
            <ArrowLeftOutlined aria-hidden="true" /> {t(`${I18N_BASE}.back`)}
          </button>
          <div className="stop-activity__title">{title}</div>
          <div className="stop-activity__crumbs">
            <span className="route-report-num">{detail.stop.code}</span>
            <span aria-hidden="true"> · </span>
            <span>{detail.stop.label || detail.stop.code}</span>
          </div>
        </div>
      }
    >
      {isPop ? (
        <div className="stop-steps">
          {detail.form.steps.map((step) => {
            /* Dos preguntas distintas sobre el mismo paso, y confundirlas es lo
               que lo dejaba negando su propio contenido:

               - ¿se respondió? Para un paso de materiales eso es si se DEJÓ
                 material; su contenido no vive en el `answer`.
               - ¿hay algo que mostrar? Eso incluye lo SUGERIDO, que la tabla
                 pinta aunque no se haya entregado nada.

               Una parada donde se sugirió material y no se dejó ninguno responde
               "no" a la primera y "sí" a la segunda: lleva el tag "Sin
               responder", que es cierto, pero no el trazo punteado de un bloque
               vacío, porque debajo hay una tabla con cantidades. */
            const isMaterials = step.type === 'materials';

            const unanswered = isMaterials
              ? !hasDeliveredMaterials(detail.materials)
              : step.answer === null;

            const empty = isMaterials
              ? !hasStopMaterials(detail.materials)
              : step.answer === null;

            return (
              <div
                className={['stop-step', empty ? 'stop-step--unanswered' : '']
                  .filter(Boolean)
                  .join(' ')}
                key={step.id}
              >
                <div className="stop-step__head">
                  <span className="stop-step__icon">
                    {STEP_ICONS[step.type] ?? <FileTextOutlined />}
                  </span>
                  <span className="stop-step__label">
                    <span className="stop-step__position">
                      {step.position}.
                    </span>{' '}
                    {step.label}
                  </span>
                  {unanswered && <Tag>{t(`${I18N_BASE}.unanswered`)}</Tag>}
                </div>
                <div className="stop-step__body">
                  <StepBody step={step} detail={detail} />
                </div>
              </div>
            );
          })}

          {/* Material dejado en el local que la plantilla no declara en ningún
              paso. Sin esta sección esa evidencia no tendría dónde verse: los
              materiales sólo se pintan dentro del paso de tipo `materials`, y
              si la plantilla no lo trae, lo entregado quedaría registrado en el
              payload y ausente de la pantalla. */}
          {!hasMaterialsStep(detail.form.steps) &&
            hasStopMaterials(detail.materials) && (
              <div className="stop-section">
                <div className="stop-section__title">
                  {t(`${I18N_BASE}.materials_without_step`)}
                </div>
                <StopMaterialsTable materials={detail.materials} />
              </div>
            )}
        </div>
      ) : (
        incident && (
          <>
            <div className="stop-section">
              <div className="stop-section__title">
                {t(`${I18N_BASE}.incident.title`)}
                <span className="stop-section__count">{incident.code}</span>
              </div>
              <div
                className={[
                  'stop-incident',
                  incident.usedForBypass ? 'stop-incident--bypass' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="stop-incident__reason">{incident.reason}</span>
                <span className="stop-incident__marked">
                  {t(`${I18N_BASE}.incident.marked_at`, {
                    time: formatTime(incident.markedAt, userTimeZone),
                    date: formatDate(incident.markedAt, userTimeZone),
                  })}
                </span>
              </div>
            </div>

            <div className="stop-section">
              <div className="stop-section__title">
                {t(`${I18N_BASE}.incident.comment_title`)}
              </div>
              {incident.note ? (
                <div className="stop-step__text">{incident.note}</div>
              ) : (
                <div className="stop-empty stop-empty--muted">
                  {t(`${I18N_BASE}.incident.no_comment`)}
                </div>
              )}
            </div>

            <div className="stop-section">
              <div className="stop-section__title">
                {t(`${I18N_BASE}.incident.photos_title`)}
                {incident.photos.length > 0 && (
                  <span className="stop-section__count">
                    {incident.photos.length}
                  </span>
                )}
              </div>
              {incident.photos.length > 0 ? (
                <StopPhotoGrid photos={incident.photos} />
              ) : (
                /* `requiresPhoto` es una propiedad del catálogo y no se muestra
                   como dato suelto, pero sí decide cómo se redacta este vacío:
                   una foto que el catálogo exigía y no llegó es una ausencia
                   que vale la pena nombrar. */
                <div className="stop-empty stop-empty--muted">
                  {t(
                    `${I18N_BASE}.incident.${
                      incident.requiresPhoto
                        ? 'no_photos_required'
                        : 'no_photos_optional'
                    }`
                  )}
                </div>
              )}
            </div>
          </>
        )
      )}
    </Modal>
  );
}

export default StopActivityModal;
